import classNames from 'classnames';
import { Formik } from 'formik';
import React, { FC, useContext } from 'react';
import * as Yup from 'yup';

import { Alert, Button, ModalContent, ModalLoading } from '@clearcover/atlas';
import {
  AllowedPaymentMethod,
  GetAllowedPaymentMethodsResponseType,
  GetPaymentMethodConfigResponseType,
  PaymentsAndBillingInfoType,
} from '@clearcover/core-app-types';

import { RadioButtonGroup } from 'Shared/components/FormInput/Variants';
import { useApi, useLazyApi } from 'Shared/services/Api';

import {
  SubmitNewPaymentMethodType,
  addPaymentMethodInitialValues,
} from 'Policies/components/AddPaymentMethodForm';
import { AdyenAddPaymentForm } from 'Policies/components/AddPaymentMethodForm/AdyenAddPaymentForm';
import { PolicyContext } from 'Policies/utils';
import { useNewApiV2 } from 'Shared/services/Api/makeApiHttp';
import { isPresent } from 'Shared/utils';
import styles from './styles.module.css';

type AddDefaultPaymentMethodFormPropsType = {
  policyId: string;
  close(): void;
  onSuccess(message: string): void;
  billingInfo?: PaymentsAndBillingInfoType;
};

export const AddDefaultPaymentMethodForm: FC<
  AddDefaultPaymentMethodFormPropsType
> = ({ close, onSuccess, policyId, billingInfo }) => {
  const {
    person,
    details: { configDiscoveryParams },
    refetchHandler,
  } = useContext(PolicyContext);

  const [{ data, loading }] = useApi<GetPaymentMethodConfigResponseType>(
    `/payments/config?configDiscoveryParams=${configDiscoveryParams}`
  );

  const [, addPaymentMethod] = useLazyApi<{ paymentMethodId: string }>({
    url: `/policies/${policyId}/addPaymentMethod`,
    method: 'POST',
  });

  const [, setDefaultPaymentMethod] = useLazyApi({
    url: 'payments/setDefaultPaymentMethod',
    method: 'POST',
  });

  const [{ data: allowedPaymentMethods, loading: loadingAllowedMethods }] =
    useNewApiV2<GetAllowedPaymentMethodsResponseType>({
      url: `/payments/allowedPaymentMethods?policyId=${policyId}&configDiscoveryParams=${configDiscoveryParams}`,
    });

  const PAYMENT_METHOD_FORM_SCHEMA = Yup.object({
    type: Yup.string().required('Required'),
  });

  const submitForm: SubmitNewPaymentMethodType = (values) => {
    return addPaymentMethod({ data: { paymentMethod: values } }).then(
      (response) => {
        return setDefaultPaymentMethod({
          data: {
            invoiceId: billingInfo?.invoiceId,
            paymentMethodId: response.data.paymentMethodId,
          },
        }).then(() => {
          refetchHandler();
          onSuccess('Payment method has been successfully updated.');
          close();
        });
      }
    );
  };

  const paymentMethodOptions = loadingAllowedMethods
    ? {}
    : (allowedPaymentMethods?.reduce(
        (acc: {}, method: AllowedPaymentMethod) => {
          return {
            ...acc,
            [method.type]: method.label,
          };
        },
        {}
      ) ?? {});

  return (
    <Formik
      initialValues={addPaymentMethodInitialValues}
      onSubmit={(values, { setSubmitting, setStatus }) => {
        setStatus();
        if (values.adyen.isValid) {
          values.adyen.submit();
        } else {
          setSubmitting(false);
          setStatus('Please complete all required fields.');
        }
      }}
      validationSchema={PAYMENT_METHOD_FORM_SCHEMA}
    >
      {({ values, setFieldValue, isSubmitting, handleSubmit, status }) => (
        <div className={styles.wrapper}>
          <h4>Update payment method</h4>
          <p>
            Updating the customer's payment method will affect all future
            payments and become the customer's default payment method.
          </p>
          <ModalContent>
            {loading || loadingAllowedMethods ? (
              <ModalLoading />
            ) : (
              <div className={styles.content}>
                <RadioButtonGroup
                  className={styles.switch}
                  name="payment-options"
                  value={values.type}
                  onChange={(value) => {
                    setFieldValue('type', value);
                  }}
                  label=""
                  options={{ ...paymentMethodOptions }}
                  disabled={values.adyen === undefined}
                />
                <div
                  className={classNames(
                    'fs-exclude',
                    styles.form,
                    styles.adyenForm,
                    values.type === 'ENCRYPTED_CREDIT_CARD'
                      ? styles.adyenCreditCard
                      : styles.adyenAch
                  )}
                  data-testid="adyen-payment-form"
                >
                  <AdyenAddPaymentForm
                    paymentType={values.type}
                    name={person.name}
                    config={data.config}
                    submitForm={submitForm}
                  />
                </div>
              </div>
            )}
          </ModalContent>
          <div className={styles.actions}>
            {isPresent(status) && (
              <Alert className={styles.error} message={status} />
            )}
            <Button
              variant="secondary"
              onClick={close}
              data-testid="congrats-update-payment-method-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isSubmitting={isSubmitting}
              onClick={() => handleSubmit()}
              data-testid="congrats-update-payment-method-submit"
            >
              Update
            </Button>
          </div>
        </div>
      )}
    </Formik>
  );
};
